import type { Email_Campaigns } from "@/src/gql/graphql";
import type { DetailAction } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { DetailPageConfig } from "@/components/shared/UniversalDetailPage/types/detailPage";
import {
  emailCampaignDetailTabs,
  emailCampaignDetailDefaultTab,
} from "@/features/email-campaigns/config/email-campaign-detail-tabs";

/**
 * Strongly-typed detail/row actions for email campaigns. Read-mostly in the CRM
 * (authored/sent by the marketing pipeline), so the action set is View + Change
 * status + Ask Meridian + admin Delete. Mirrors SocialMediaPostDetailConfig.
 */
export const emailCampaignDetailActions: DetailAction<Email_Campaigns>[] = [
  {
    id: "view-details",
    label: "View Details",
    icon: "eye",
    variant: "outline",
    primary: true,
    action: "viewDetails",
  },
  {
    id: "status-dropdown",
    label: "Change Status",
    icon: "chevron-down",
    variant: "outline",
    primary: false,
    action: "changeStatus",
    tooltip: "Change this campaign's send status",
  },
  {
    id: "ask-meridian",
    label: "Ask Meridian",
    icon: "meridian",
    variant: "outline",
    primary: false,
    action: "askMeridian",
    tooltip: "Ask Meridian AI assistant about this campaign",
  },
  {
    id: "delete",
    label: "Delete",
    icon: "trash2",
    variant: "destructive",
    primary: false,
    action: "deleteItem",
    allowedRoles: ["wtc_admin"],
    tooltip: "Delete this email campaign record (admin only)",
  },
];

/**
 * Detail page configuration for email campaigns. Strongly typed (mirrors
 * SocialMediaPostDetailConfig) — the Details tab renders from the table config's
 * `fields` map via the generic FieldsTabContent renderer, so field metadata is
 * not re-declared here (single source of truth = EmailCampaignsConfig).
 */
export const EMAIL_CAMPAIGN_DETAIL_CONFIG: DetailPageConfig<Email_Campaigns> = {
  entity: "email_campaigns",
  displayName: "Email Campaign",

  titleTemplate: (item: Email_Campaigns) => item.name || item.subject || "Email Campaign",

  tabs: emailCampaignDetailTabs,
  defaultTab: emailCampaignDetailDefaultTab,
  actions: emailCampaignDetailActions,
  detailActions: emailCampaignDetailActions,

  showHeader: true,
  showActions: true,
};
